import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { investorApi, vendorApi } from '../../api/api';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import GradientButton from '../common/GradiantButton';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
 
const YourProjectImages = ({ data }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageData, setImageData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const theme = useSelector((data) => data.themeValue.colors);
 
  const scrollContainerRef = useRef(null);
 
  const handleOpen = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };
 
  const handleAddImages = () => {
    navigate("/AddImages", { state: { propertyData: data } });
  };
 
  const handleClose = () => setOpen(false);
 
  const fetchImages = async () => {
    try {
      const response = await investorApi.getProjectImagesById(data.id);
     
      setImageData(response.data);
    } catch (err) {
      console.error(err);
    }
  };
 
  const handleDelete = async (imageId) => {
    try {
      const response = await vendorApi.deleteImageById({
        type: data.type,
        id: imageId
      });
      setSnackbar({ open: true, message: 'Image deleted successfully', severity: 'success' });
      fetchImages();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete image', severity: 'error' });
    }
  };
 
  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };
 
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };
 
  // Check scroll position
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
 
  useEffect(() => {
    fetchImages();
  }, [data.id]);
 
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      // Initial check
      checkScrollPosition();
    }
   
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [imageData]);
 
  // Re-check when window resizes
  useEffect(() => {
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, []);
 
  return (
    <Box sx={{ mt: 4, width: '100%', position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.text,
            fontWeight: 600,
            fontSize: { xs: "16px", sm: "18px", md: "20px" },
          }}
        >
          Project Images
        </Typography>
 
        <GradientButton
          text="Add Images"
          onClick={handleAddImages}
          width="auto"
          height={35}
        />
      </Box>
 
      {Array.isArray(imageData) && imageData.length > 0 ? (
        <Box sx={{ position: 'relative' }}>
          {/* Left Scroll Icon */}
          {showLeftScroll && (
            <IconButton
              onClick={scrollLeft}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                width: 32,
                height: 32,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
 
          {/* Right Scroll Icon */}
          {showRightScroll && (
            <IconButton
              onClick={scrollRight}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                width: 32,
                height: 32,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
 
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: 2,
              pb: 2,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              whiteSpace: 'nowrap',
              // Hide scrollbar completely
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none', // Firefox
              '-ms-overflow-style': 'none', // IE and Edge
              // Add padding to prevent content from being hidden behind scroll icons
              px: (showLeftScroll || showRightScroll) ? 6 : 0,
            }}
          >
            {imageData.map((image, index) => (
              <Box
                key={image.id}
                sx={{
                  position: 'relative',
                  flex: '0 0 auto',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              >
                <img
                  src={image.ImageURL}
                  alt={`Image ${index}`}
                  style={{
                    height: '200px',
                    width: '100%',
                    maxWidth: '300px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                  onClick={() => handleOpen(index)}
                />
 
                <IconButton
                  onClick={() => handleDelete(image.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    '&:hover': { backgroundColor: 'rgba(255,0,0,0.8)', color: '#fff' },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography>No images available</Typography>
      )}
 
      {/* Fullscreen Modal Swiper */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
 
          <Swiper
            modules={[Navigation, Pagination, Keyboard]}
            initialSlide={selectedIndex}
            loop={true}
            navigation
            pagination={{ clickable: true }}
            keyboard={{ enabled: true }}
            style={{ width: '100%', height: '100%' }}
          >
            {Array.isArray(imageData) && imageData.length > 0 ? (
              imageData.map((image, index) => (
                <SwiperSlide key={image.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      px: 2,
                      position: 'relative',
                    }}
                  >
                    <img
                      src={image.ImageURL}
                      alt={`Slide ${index}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        borderRadius: '10px',
                      }}
                    />
 
                    <IconButton
                      onClick={() => handleDelete(image.id)}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,0,0,0.8)',
                          color: '#fff',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </SwiperSlide>
              ))
            ) : (
              <Typography sx={{ color: '#fff', textAlign: 'center', mt: 2 }}>
                No images available
              </Typography>
            )}
          </Swiper>
        </Box>
      </Modal>
 
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
 
export default YourProjectImages;

